import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Save, Loader2, Type, Briefcase, MapPin, Clock, UserCog } from "lucide-react";

import api from "../../lib/api";
import AdminLayout from "../../components/admin/AdminLayout";
import RichTextEditor from "../../components/admin/RichTextEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Quill emits "<p><br></p>" for an empty editor — treat that (and any
// whitespace-only / tag-only markup) as no content.
const isEmptyHtml = (html) => {
  if (!html) return true;
  if (/<(img|iframe|video)/i.test(html)) return false;
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, "").trim() === "";
};

// Dropdown options.
const ROLES = [
  "Engineering",
  "Sales",
  "Pre-sales",
  "HR",
  "Admin",
  "Operations",
  "DevOps",
  "Others",
];
const LOCATIONS = ["Onsite", "Remote", "Hybrid"];
const ENGAGEMENTS = ["Full time", "Part-time", "Contractual"];

// Field length standards.
const TITLE_MIN = 3;
const TITLE_MAX = 120;

// Small uppercase label with a leading icon — the "Details" style.
const FieldLabel = ({ icon: Icon, children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400"
  >
    {Icon && <Icon className="h-3.5 w-3.5" />}
    {children}
  </label>
);

const fieldClass =
  "w-full rounded-md border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15 focus:outline-none transition";

const selectTriggerClass =
  "h-auto w-full rounded-md border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm data-[placeholder]:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15";

// Contained card with an inline title.
const Card = ({ title, children }) => (
  <section className="bg-white rounded-md border border-gray-200 shadow-sm p-5 sm:p-6">
    <h2 className="text-sm font-semibold text-gray-900 mb-5">{title}</h2>
    {children}
  </section>
);

const JobPageAdd = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    job_title: "",
    role: "",
    location: "",
    engagement: "",
    job_description: "",
    is_active: true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Front-end validation — show one error at a time.
    const title = formData.job_title.trim();
    if (!title) {
      toast.error("Job title is required");
      return;
    }
    if (title.length < TITLE_MIN) {
      toast.error(`Title must be at least ${TITLE_MIN} characters`);
      return;
    }
    if (title.length > TITLE_MAX) {
      toast.error(`Title must be ${TITLE_MAX} characters or less`);
      return;
    }
    if (!formData.role.trim()) {
      toast.error("Role is required");
      return;
    }
    if (!formData.location) {
      toast.error("Location is required");
      return;
    }
    if (!formData.engagement) {
      toast.error("Engagement type is required");
      return;
    }
    if (isEmptyHtml(formData.job_description)) {
      toast.error("Job description is required");
      return;
    }

    setSaving(true);

    const payload = {
      job_title: formData.job_title,
      role: formData.role,
      location: formData.location,
      engagement: formData.engagement,
      job_description: formData.job_description,
      is_active: formData.is_active ? 1 : 0,
    };

    try {
      const response = await api.post("/job-posts/add", payload);
      toast.success(response.data?.message || "Job post saved successfully");
      navigate("/admin/jobs");
    } catch (error) {
      const result = error.response?.data;
      if (result?.errors) {
        const firstError = Object.values(result.errors)[0];
        toast.error(firstError?.[0] || "Failed to save job post");
      } else {
        toast.error(result?.message || "Failed to save job post");
      }
    } finally {
      setSaving(false);
    }
  };

  const headerActions = (
    <>
      <button
        type="button"
        onClick={() => navigate("/admin/jobs")}
        className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
      >
        Cancel
      </button>
      <button
        type="submit"
        form="job-form"
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#b32126] hover:bg-[#8f1a1f] shadow-sm shadow-[#b32126]/20 disabled:opacity-60 transition-colors"
      >
        {saving ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        {saving ? "Saving…" : "Save Job"}
      </button>
    </>
  );

  return (
    <AdminLayout
      title="Add Job"
      subtitle="Create and publish a new job opening"
      actions={headerActions}
    >
      <form id="job-form" onSubmit={handleSubmit}>
        {/* Two equal columns */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* ---------- Left: job details ---------- */}
          <Card title="Job Details">
            <div className="space-y-5">
              {/* Title */}
              <div>
                <FieldLabel icon={Type} htmlFor="job_title">
                  Job Title
                </FieldLabel>
                <input
                  id="job_title"
                  type="text"
                  name="job_title"
                  placeholder="e.g. Senior Backend Engineer"
                  value={formData.job_title}
                  onChange={handleChange}
                  maxLength={TITLE_MAX}
                  className={fieldClass}
                />
              </div>

              {/* Role */}
              <div>
                <FieldLabel icon={UserCog} htmlFor="role">
                  Role
                </FieldLabel>
                <Select
                  value={formData.role}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, role: value }))
                  }
                >
                  <SelectTrigger id="role" className={selectTriggerClass}>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* ---------- Right: employment ---------- */}
          <Card title="Employment">
            <div className="space-y-5">
              {/* Location */}
              <div>
                <FieldLabel icon={MapPin} htmlFor="location">
                  Location
                </FieldLabel>
                <Select
                  value={formData.location}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, location: value }))
                  }
                >
                  <SelectTrigger id="location" className={selectTriggerClass}>
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map((l) => (
                      <SelectItem key={l} value={l}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Engagement */}
              <div>
                <FieldLabel icon={Clock} htmlFor="engagement">
                  Engagement
                </FieldLabel>
                <Select
                  value={formData.engagement}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, engagement: value }))
                  }
                >
                  <SelectTrigger id="engagement" className={selectTriggerClass}>
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENGAGEMENTS.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div>
                <FieldLabel icon={Briefcase}>Status</FieldLabel>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: !prev.is_active,
                      }))
                    }
                    title={formData.is_active ? "Active" : "Inactive"}
                    className={`relative h-6 w-11 rounded-full transition-colors ${
                      formData.is_active ? "bg-green-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        formData.is_active ? "translate-x-5" : ""
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-600">
                    {formData.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* ---------- Description ---------- */}
        <div className="mt-6">
          <Card title="Job Description">
            <div className="rounded-md border border-gray-200 bg-gray-50 overflow-hidden focus-within:border-[#b32126] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#b32126]/15 transition">
              <RichTextEditor
                value={formData.job_description}
                placeholder="Describe the role, responsibilities, requirements…"
                onChange={(html) =>
                  setFormData((prev) => ({
                    ...prev,
                    job_description: isEmptyHtml(html) ? "" : html,
                  }))
                }
              />
            </div>
          </Card>
        </div>
      </form>
    </AdminLayout>
  );
};

export default JobPageAdd;
